import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from '@material-ui/core';
import { TabPanelContent } from './BatchPopup.styled';
import { useTranslation } from 'react-i18next';
import ChevronUp from 'src/assets/images/chevronBlue-up.svg';
import TruncatedTooltip from 'src/common/organisms/listView/TruncatedTooltip';
import {
    SetContentHeader,
    DesktopTableWrap,
    SetContentTable,
    SetContentTableHeader,
    SetContentTableHeaderCell,
    SetContentTableBody,
    SetContentTableRow,
    SetContentTableCell,
    DescriptionText,
    DescriptionTooltipWrapper,
    SetContentEmptyMessage,
    MobileCardList,
    MobileCard,
    MobileCardHeader,
    MobileCardLeft,
    MobileCardChevron,
    MobileCardTitle,
    MobileCardBody,
    MobileCardField,
    MobileCardFieldRow,
    MobileFieldLabel,
    MobileFieldValue,
} from './SetContentTab.styled';
import {FONT_FAMILY_DEMI} from 'src/styles/fonts/MCFontConstants';
import { sizes } from 'src/styles/Media.styled';
import { formatPopupDate } from './formatPopupDate';

const DESCRIPTION_MAX_LENGTH = 26;
const TABLET_DESCRIPTION_MAX_LENGTH = 12;

const renderDescriptionCell = (description, maxLength) => {

    if (!description) {
        return <DescriptionText />;
    }

    const safeDescription = String(description);

    return (
        <DescriptionText>
            <DescriptionTooltipWrapper $maxLength={maxLength}>
                <TruncatedTooltip
                    text={safeDescription}
                    maxLength={maxLength}
                    productCfn=""
                />
            </DescriptionTooltipWrapper>
        </DescriptionText>
    );
};

const SetContentTab = ({ content }) => {
    const { t } = useTranslation();
    const dateFormat = t('dateFormat');
    const isJapanUser = localStorage.getItem('countryISOCode') === 'JP';
    const [expandedCard, setExpandedCard] = useState(null);
    const isTabletViewport = useMediaQuery(`(max-width:${sizes.ipadAir}px)`);
    const useShortYear = useMediaQuery(
        `(min-width:${sizes.smalDevice}px) and (max-width:${sizes.ipad9thGenHeight}px)`
    );
    const descriptionMaxLength = isTabletViewport
        ? TABLET_DESCRIPTION_MAX_LENGTH
        : DESCRIPTION_MAX_LENGTH;

    if (!content || (Array.isArray(content) && !content.length)) {
        return (
            <TabPanelContent
                $background="#ffffff"
                $padding="16px 20px"
                $borderRadius="6px"
                $setContent={true}
            >
                <SetContentEmptyMessage>No set content available.</SetContentEmptyMessage>
            </TabPanelContent>
        );
    }

    const items = Array.isArray(content) ? content : [content];

    const toggleCard = (index) => {
        setExpandedCard(expandedCard === index ? null : index);
    };

    return (
        <TabPanelContent
            $background="#ffffff"
            $padding="16px"
            $borderRadius="6px"
            $setContent={true}
        >
            <SetContentHeader>{t('setContent.title')}</SetContentHeader>
            <DesktopTableWrap>
                <SetContentTable>
                    <SetContentTableHeader>
                        <SetContentTableRow $isHeader>
                            <SetContentTableHeaderCell width="14%" tabWidth="14%">{t('setContent.product')}</SetContentTableHeaderCell>
                            <SetContentTableHeaderCell width="12%" tabWidth="14%">{t('setContent.batchOrLot')}</SetContentTableHeaderCell>
                            <SetContentTableHeaderCell width="20%" tabWidth="20%">{t('setContent.description')}</SetContentTableHeaderCell>
                            <SetContentTableHeaderCell width="8%" tabWidth="8%" textAlign='center'>{t('setContent.actual')}</SetContentTableHeaderCell>
                            <SetContentTableHeaderCell width="8%" tabWidth="8%" textAlign='center'>{t('setContent.bom')}</SetContentTableHeaderCell>
                            <SetContentTableHeaderCell width="8%" tabWidth="8%" textAlign='center'>{t('setContent.critical')}</SetContentTableHeaderCell>
                            <SetContentTableHeaderCell width="8%" tabWidth={isJapanUser ? '12%' : '8%'} textAlign='center'>
                                {t('setContent.setHome')}
                            </SetContentTableHeaderCell>
                            <SetContentTableHeaderCell width="8%" tabWidth={isJapanUser ? '10%' : '8%'} textAlign='center'>{t('setContent.batchQty')}</SetContentTableHeaderCell>
                            <SetContentTableHeaderCell width="14%" tabWidth={isJapanUser ? '10%' : '14%'} $dateColumn>
                                {t('setContent.useByDate')}
                            </SetContentTableHeaderCell>
                        </SetContentTableRow>
                    </SetContentTableHeader>
                    <SetContentTableBody>
                        {items.map((item, index) => {
                            const rowKey = item.id || item.itemNumber || item.productNumber || `set-content-${index}`;

                            return (
                                <SetContentTableRow key={rowKey}>
                                    <SetContentTableCell $fontFamily={FONT_FAMILY_DEMI} data-label="Product#">
                                        {item.itemNumber || item.productNumber}
                                    </SetContentTableCell>
                                    <SetContentTableCell $fontFamily={FONT_FAMILY_DEMI} data-label="Batch/Lot #">
                                        {item.itemView?.lotBatchSerialNumber}
                                    </SetContentTableCell>
                                    <SetContentTableCell data-label="Description">
                                        {renderDescriptionCell(item.itemDescription, descriptionMaxLength)}
                                    </SetContentTableCell>
                                    <SetContentTableCell $textAlign={'center'} data-label="Actual">
                                        {item.actualQuantity}
                                    </SetContentTableCell>
                                    <SetContentTableCell $textAlign={'center'} data-label="Bom">
                                        {item.bomQuantity}
                                    </SetContentTableCell>
                                    <SetContentTableCell $textAlign={'center'} data-label="Critical">
                                        {item.minimumQuantity}
                                    </SetContentTableCell>
                                    <SetContentTableCell $textAlign={'center'} data-label="Set Home">
                                        {item.stockQuantity}
                                    </SetContentTableCell>
                                    <SetContentTableCell $textAlign={'center'} data-label="Batch Qty">
                                        {item.itemView?.onHandQuantity}
                                    </SetContentTableCell>
                                    <SetContentTableCell $dateColumn $noWrap data-label="Use by Date">
                                        {formatPopupDate(item.itemView?.useByDate, dateFormat, useShortYear)}
                                    </SetContentTableCell>
                                </SetContentTableRow>
                            );
                        })}
                    </SetContentTableBody>
                </SetContentTable>
            </DesktopTableWrap>

            <MobileCardList>
                {items.map((item, index) => {
                    const rowKey = item.id || item.itemNumber || item.productNumber || `set-content-${index}`;
                    const isExpanded = expandedCard === index;

                    return (
                        <MobileCard
                            key={rowKey}
                            $isEven={index % 2 === 0}
                            onClick={() => toggleCard(index)}
                        >
                            <MobileCardHeader>
                                <MobileCardLeft>
                                    <MobileCardChevron $expanded={isExpanded}>
                                        <img src={ChevronUp} alt="toggle" width="20" height="20" />
                                    </MobileCardChevron>
                                    <MobileCardTitle>{item.itemNumber || item.productNumber}</MobileCardTitle>
                                </MobileCardLeft>
                            </MobileCardHeader>

                            {isExpanded && (
                                <MobileCardBody>
                                    <MobileCardField>
                                        <MobileCardFieldRow>
                                            <MobileFieldValue>{item.itemDescription}</MobileFieldValue>
                                        </MobileCardFieldRow>
                                    </MobileCardField>
                                    <MobileCardField>
                                        <MobileCardFieldRow>
                                            <MobileFieldLabel>{t('setContent.actual')}:</MobileFieldLabel>
                                            <MobileFieldValue>{item.actualQuantity}</MobileFieldValue>
                                        </MobileCardFieldRow>
                                    </MobileCardField>
                                    <MobileCardField>
                                        <MobileCardFieldRow>
                                            <MobileFieldLabel>{t('setContent.bom')}:</MobileFieldLabel>
                                            <MobileFieldValue>{item.bomQuantity}</MobileFieldValue>
                                        </MobileCardFieldRow>
                                    </MobileCardField>
                                    <MobileCardField>
                                        <MobileCardFieldRow>
                                            <MobileFieldLabel>{t('setContent.critical')}:</MobileFieldLabel>
                                            <MobileFieldValue>{item.minimumQuantity}</MobileFieldValue>
                                        </MobileCardFieldRow>
                                    </MobileCardField>
                                    <MobileCardField>
                                        <MobileCardFieldRow>
                                            <MobileFieldLabel>{t('setContent.setHome')}:</MobileFieldLabel>
                                            <MobileFieldValue>{item.stockQuantity}</MobileFieldValue>
                                        </MobileCardFieldRow>
                                    </MobileCardField>
                                    <MobileCardField>
                                        <MobileCardFieldRow>
                                            <MobileFieldLabel>{t('setContent.batchQty')}:</MobileFieldLabel>
                                            <MobileFieldValue>{item.itemView?.onHandQuantity}</MobileFieldValue>
                                        </MobileCardFieldRow>
                                    </MobileCardField>
                                    <MobileCardField>
                                        <MobileCardFieldRow>
                                            <MobileFieldLabel>{t('setContent.useByDate')}:</MobileFieldLabel>
                                            <MobileFieldValue>{formatPopupDate(item.itemView?.useByDate, dateFormat)}</MobileFieldValue>
                                        </MobileCardFieldRow>
                                    </MobileCardField>
                                </MobileCardBody>
                            )}
                        </MobileCard>
                    );
                })}
            </MobileCardList>
        </TabPanelContent>
    );
};

SetContentTab.propTypes = {
    content: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.array,
        PropTypes.object,
        PropTypes.string,
        PropTypes.number,
    ]),
};

SetContentTab.defaultProps = {
    content: null,
};

export default SetContentTab;